import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Target, Eye, Heart } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="about" ref={sectionRef} className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 pb-2 bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent">
            {t('about.title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Mission */}
            <div className={`group transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{ transitionDelay: '200ms' }}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-900/20 flex items-center justify-center text-red-500 group-hover:bg-red-900/40 transition-colors">
                  <Target size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors">
                    {t('about.mission')}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {t('about.missionText')}
                  </p>
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className={`group transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{ transitionDelay: '400ms' }}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-900/20 flex items-center justify-center text-red-500 group-hover:bg-red-900/40 transition-colors">
                  <Eye size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors">
                    {t('about.vision')}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {t('about.visionText')}
                  </p>
                </div>
              </div>
            </div>

            {/* Values */}
            <div className={`group transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{ transitionDelay: '600ms' }}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-900/20 flex items-center justify-center text-red-500 group-hover:bg-red-900/40 transition-colors">
                  <Heart size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors">
                    {t('about.passion')}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {t('about.passionText')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className={`relative overflow-hidden transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{ transitionDelay: '300ms' }}>
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1552820728-8b83bb6b773f?w=800&q=80"
                alt="Studio workspace"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
            </div>
            {/* Decorative Element - Hidden on mobile to prevent overflow */}
            <div className="hidden md:block absolute -bottom-6 -right-6 w-48 h-48 bg-red-900/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;